import React, { useMemo, useRef, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { colorForDoctor, doctorDisplayName } from './doctorColors';
import { STATUS_META, statusLabel } from './appointmentStatus';
import { useRescheduleAppointmentMutation } from '../../api/appointmentsApi';
import { apiErrorMessage } from '../../utils/apiError';
import { useIsMobile } from '../../hooks/useIsMobile';

// Visible hour range
const START_HOUR = 8;            // 8:00 AM
const END_HOUR = 22;             // boundary — last label is 9 PM (21)
const BUSINESS_OPEN_HOUR = 9;   // 9 AM — before this is off-hours
const BUSINESS_CLOSE_HOUR = 20; // 8 PM — at/after is off-hours

// px per hour (half-hour rows are half this). Taller on mobile so slots aren't cramped and their
// text stays readable; the wrapper scrolls vertically to keep the whole day reachable.
const HOUR_HEIGHT_DESKTOP = 60;
const HOUR_HEIGHT_MOBILE = 96;

// Day-column min width. On mobile we force a generous width (and let the grid scroll horizontally)
// so columns never squash into an unreadable sliver.
const COLUMN_WIDTHS = { compact: 100, normal: 160, wide: 240 };
const COLUMN_WIDTH_MOBILE = 150;

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function isSameDay(a, b) { return a.toDateString() === b.toDateString(); }
function fmtTime(d) {
  return d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit' });
}

function viewDayCount(view) {
  switch (view) {
    case 'day':      return 1;
    case '3day':     return 3;
    case 'week':     return 6;
    case 'month':    return 30;
    case 'doctor':   return 1;  // doctor-wise = one day, N doctor columns
    default:         return 6;
  }
}

/**
 * Cluster-based lane assignment within ONE column.
 * Returns Map<apptId, { lane: number, totalLanes: number }>.
 */
function assignLanes(colAppts) {
  const result = new Map();
  if (!colAppts.length) return result;

  const sorted = [...colAppts].sort((a, b) => {
    const ta = new Date(a.scheduledAt).getTime();
    const tb = new Date(b.scheduledAt).getTime();
    if (ta !== tb) return ta - tb;
    return (b.durationMinutes ?? 30) - (a.durationMinutes ?? 30);
  });

  let cluster = [];
  let clusterEnd = 0;

  const finishCluster = () => {
    if (!cluster.length) return;
    const lanes = [];
    const apptLane = new Map();
    for (const a of cluster) {
      const s = new Date(a.scheduledAt).getTime();
      const e = s + Math.max(15, a.durationMinutes ?? 30) * 60_000;
      let idx = lanes.findIndex((laneEnd) => laneEnd <= s);
      if (idx === -1) { idx = lanes.length; lanes.push(e); }
      else { lanes[idx] = e; }
      apptLane.set(a.id, idx);
    }
    const totalLanes = lanes.length;
    for (const a of cluster) {
      result.set(a.id, { lane: apptLane.get(a.id), totalLanes });
    }
    cluster = [];
    clusterEnd = 0;
  };

  for (const a of sorted) {
    const s = new Date(a.scheduledAt).getTime();
    const e = s + Math.max(15, a.durationMinutes ?? 30) * 60_000;
    if (cluster.length === 0 || s < clusterEnd) {
      cluster.push(a);
      clusterEnd = Math.max(clusterEnd, e);
    } else {
      finishCluster();
      cluster.push(a);
      clusterEnd = e;
    }
  }
  finishCluster();

  return result;
}

function fmtDurationShort(mins) {
  const m = Math.max(0, Number(mins) || 0);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
}

export default function CalendarGrid({
  view,
  rangeStart,
  appointments,
  selectedDoctorId,
  doctors,
  columnWidth,
  onSlotClick,
  onAppointmentClick,
  breakStartMin = null,
  breakEndMin = null,
  breakLabel = 'Break',
}) {
  const hasBreak = breakStartMin != null && breakEndMin != null && breakEndMin > breakStartMin;

  // Live-updating clock — drives the now-line position. Ticks every 30 s so the
  // indicator moves smoothly across the grid without hammering re-renders.
  // `mountedAt` is a stable ref so the initial scroll only fires once (on mount /
  // day-change), not on every 30-second tick.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  const mountedAt = useRef(new Date());

  const isMobile = useIsMobile();
  const HOUR_HEIGHT = isMobile ? HOUR_HEIGHT_MOBILE : HOUR_HEIGHT_DESKTOP;
  const colMinWidth = isMobile
    ? COLUMN_WIDTH_MOBILE
    : (COLUMN_WIDTHS[columnWidth] ?? COLUMN_WIDTHS.normal);

  const isDoctorView = view === 'doctor';

  // Days array — for doctor view there's only one day (today/rangeStart) but multiple doctor columns
  const days = useMemo(() => {
    const count = isDoctorView ? 1 : viewDayCount(view);
    return Array.from({ length: count }, (_, i) => addDays(startOfDay(rangeStart), i));
  }, [rangeStart, view, isDoctorView]);

  // Column model:
  //  - For day/3day/week/month view: each column is a day. `key` = day.toDateString(), `dayDate` = day.
  //  - For doctor view: each column is a doctor on the single visible day. `key` = doctorId, `dayDate` = days[0], `doctorId` set.
  const columns = useMemo(() => {
    if (isDoctorView) {
      const list = selectedDoctorId
        ? (doctors ?? []).filter((d) => d.id === selectedDoctorId)
        : (doctors ?? []);
      return list.map((d) => ({
        key:    `doc-${d.id}`,
        dayDate: days[0],
        doctorId: d.id,
        label: doctorDisplayName(d.fullName),
        labelSub: d.specialization ?? '',
      }));
    }
    return days.map((d) => ({
      key: d.toDateString(),
      dayDate: d,
      doctorId: null,
      label: d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', day: '2-digit', month: 'short' }).toUpperCase(),
      labelSub: '',
    }));
  }, [days, doctors, selectedDoctorId, isDoctorView]);

  // Filter appointments based on view + selected doctor
  const filteredAppts = useMemo(() => {
    let list = appointments ?? [];
    if (selectedDoctorId && !isDoctorView) list = list.filter((a) => a.assignedUserId === selectedDoctorId);
    return list;
  }, [appointments, selectedDoctorId, isDoctorView]);

  // Group appointments by COLUMN
  const apptsByColumn = useMemo(() => {
    const m = new Map();
    for (const col of columns) m.set(col.key, []);
    for (const a of filteredAppts) {
      const apptDayKey = new Date(a.scheduledAt).toDateString();
      if (isDoctorView) {
        const dayMatch = days[0] && apptDayKey === days[0].toDateString();
        if (!dayMatch) continue;
        const colKey = `doc-${a.assignedUserId}`;
        if (m.has(colKey)) m.get(colKey).push(a);
      } else {
        if (m.has(apptDayKey)) m.get(apptDayKey).push(a);
      }
    }
    return m;
  }, [filteredAppts, columns, days, isDoctorView]);

  const lanesByColumn = useMemo(() => {
    const m = new Map();
    for (const col of columns) {
      m.set(col.key, assignLanes(apptsByColumn.get(col.key) ?? []));
    }
    return m;
  }, [columns, apptsByColumn]);

  const doctorNameById = useMemo(() => {
    const m = new Map();
    (doctors ?? []).forEach((d) => m.set(d.id, d.fullName));
    return m;
  }, [doctors]);

  const doctorTagById = useMemo(() => {
    const m = new Map();
    (doctors ?? []).forEach((d) => m.set(d.id, d.colorTag));
    return m;
  }, [doctors]);

  // Include seconds so the line starts at the correct sub-minute position on mount
  // and stays accurate between 30-second ticks.
  const nowMinutes = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
  const nowInWindow = nowMinutes >= START_HOUR * 60 && nowMinutes <= END_HOUR * 60;
  const nowTop = ((nowMinutes - START_HOUR * 60) / 60) * HOUR_HEIGHT;
  const todayVisible = days.some((d) => isSameDay(d, now));
  const showNow = nowInWindow && todayVisible;

  // mutation to reschedule via drag-drop
  const [rescheduleAppointment] = useRescheduleAppointmentMutation();

  const gridRef = useRef(null);
  // Scroll to current hour when the visible day range changes (navigation / mount).
  // Intentionally does NOT depend on `now` — the 30-second clock tick must not
  // force the grid to scroll out from wherever the user has manually scrolled to.
  useEffect(() => {
    if (!gridRef.current) return;
    if (!days.some((d) => isSameDay(d, mountedAt.current))) return;
    const targetHour = Math.max(START_HOUR, Math.min(END_HOUR - 1, mountedAt.current.getHours() - 1));
    const offset = (targetHour - START_HOUR) * HOUR_HEIGHT;
    gridRef.current.scrollTop = Math.max(0, offset - 60);
  }, [days]); // only re-scroll on day-range change, not on every clock tick

  const totalHalfHourRows = (END_HOUR - START_HOUR) * 2;
  const ROW_HEIGHT = HOUR_HEIGHT / 2;
  const TIME_ROW_START = 3;
  const overlayHeight = totalHalfHourRows * ROW_HEIGHT;
  const rightClickGap = 14;

  return (
    <div className="cal-grid-wrap" ref={gridRef}>
      <div
        className="cal-grid"
        style={{
          '--cal-days': columns.length,
          '--cal-day-min-width': colMinWidth + 'px',
          '--cal-row-height': ROW_HEIGHT + 'px',
        }}
      >
        {/* Row 1: corner + column headers */}
        <div className="cal-time-header" style={{ gridColumn: 1, gridRow: 1 }} />
        {columns.map((col, ci) => {
          const todayMatch = isSameDay(col.dayDate, now);
          const dow = col.dayDate.getDay();
          const weekend = dow === 0 || dow === 6;
          return (
            <div
              key={`hdr-${col.key}`}
              className={`cal-day-header ${todayMatch ? 'today' : ''} ${weekend ? 'weekend' : ''}`}
              style={{ gridColumn: ci + 2, gridRow: 1 }}
              title={col.label + (col.labelSub ? ` — ${col.labelSub}` : '')}
            >
              {col.label}
              {col.labelSub && <span className="cal-day-header-sub"> · {col.labelSub}</span>}
            </div>
          );
        })}

        {/* Row 2: all-day strip */}
        <div className="cal-allday-label" style={{ gridColumn: 1, gridRow: 2 }}>
          all-day
        </div>
        {columns.map((col, ci) => {
          const todayMatch = isSameDay(col.dayDate, now);
          const dow = col.dayDate.getDay();
          const weekend = dow === 0 || dow === 6;
          return (
            <div
              key={`allday-${col.key}`}
              className={`cal-allday-cell ${todayMatch ? 'today' : ''} ${weekend ? 'weekend' : ''}`}
              style={{ gridColumn: ci + 2, gridRow: 2 }}
            />
          );
        })}

        {/* Time slot cells */}
        {Array.from({ length: totalHalfHourRows }, (_, idx) => {
          const hour = START_HOUR + Math.floor(idx / 2);
          const minute = idx % 2 === 0 ? 0 : 30;
          const isHourStart = minute === 0;
          const offHours = hour < BUSINESS_OPEN_HOUR || hour >= BUSINESS_CLOSE_HOUR;
          const slotStartMin = hour * 60 + minute;
          const inBreak = hasBreak && slotStartMin < breakEndMin && (slotStartMin + 30) > breakStartMin;
          const gridRow = TIME_ROW_START + idx;

          return (
            <React.Fragment key={`row-${idx}`}>
              <div className="cal-time-label" style={{ gridColumn: 1, gridRow }}>
                {isHourStart ? fmtTime(new Date(2000, 0, 1, hour, 0)) : ''}
              </div>

              {columns.map((col, ci) => {
                const todayMatch = isSameDay(col.dayDate, now);
                const dow = col.dayDate.getDay();
                const weekend = dow === 0 || dow === 6;
                const cls = [
                  'cal-cell',
                  isHourStart ? 'hour-divider' : '',
                  todayMatch ? 'today' : '',
                  weekend ? 'weekend' : '',
                  offHours ? 'off-hours' : '',
                  inBreak ? 'cal-break-cell' : '',
                ].filter(Boolean).join(' ');
                return (
                  <div
                    key={`c-${col.key}-${idx}`}
                    className={cls}
                    style={{ gridColumn: ci + 2, gridRow }}
                    title={inBreak ? `${breakLabel} — booking during the clinic break` : undefined}
                    onClick={() => {
                      // The break is shown on the calendar but does NOT block booking — staff can
                      // still add an appointment during the break; we just flag it for awareness.
                      if (inBreak) toast.info(`${breakLabel}: booking during the clinic break.`);
                      onSlotClick(col.dayDate, hour, minute, { doctorId: col.doctorId });
                    }}
                    onDragOver={(e) => { e.preventDefault(); }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      try {
                        const raw = e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text');
                        if (!raw) return;
                        const payload = JSON.parse(raw);
                        const apptId = payload?.id;
                        if (!apptId) return;
                        const appt = filteredAppts.find((x) => x.id === apptId);
                        if (!appt) return toast.error('Appointment not found');

                        const dt = new Date(col.dayDate);
                        dt.setHours(hour, minute, 0, 0);
                        const newIso = dt.toISOString();
                        const oldIso = new Date(appt.scheduledAt).toISOString();
                        if (newIso === oldIso) return toast.info('Appointment already at this time');

                        const ok = window.confirm(`Move ${appt.patientName ?? 'appointment'} to ${dt.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}?`);
                        if (!ok) return;

                        await rescheduleAppointment({ id: appt.id, newScheduledAt: newIso, newDurationMinutes: appt.durationMinutes ?? 30, reason: 'Moved on calendar' }).unwrap();
                        toast.success('Appointment moved');
                      } catch (err) {
                        toast.error(apiErrorMessage(err, 'Could not move appointment'));
                      }
                    }}
                  />
                );
              })}
            </React.Fragment>
          );
        })}

        {/* Time gutter "now" arrow */}
        <div
          className="cal-time-overlay"
          style={{
            gridColumn: 1,
            gridRow: `${TIME_ROW_START} / span ${totalHalfHourRows}`,
            height: overlayHeight + 'px',
          }}
        >
          {showNow && <div className="cal-now-arrow" style={{ top: nowTop + 'px' }} aria-hidden />}
        </div>

        {/* Per-column appointment overlays */}
        {columns.map((col, ci) => {
          const colAppts = apptsByColumn.get(col.key) ?? [];
          const lanes = lanesByColumn.get(col.key) ?? new Map();
          const isToday = isSameDay(col.dayDate, now);

          return (
            <div
              key={`overlay-${col.key}`}
              className="cal-day-overlay"
              style={{
                '--cal-right-gap': `${rightClickGap}px`,
                gridColumn: ci + 2,
                gridRow: `${TIME_ROW_START} / span ${totalHalfHourRows}`,
                height: overlayHeight + 'px',
              }}
            >
              {isToday && showNow && (
                <div className="cal-now-line" style={{ top: nowTop + 'px' }} aria-hidden />
              )}

              {hasBreak && (() => {
                const bTop = ((breakStartMin - START_HOUR * 60) / 60) * HOUR_HEIGHT;
                const bH = ((breakEndMin - breakStartMin) / 60) * HOUR_HEIGHT;
                if (bTop + bH <= 0 || bTop >= overlayHeight) return null;
                const top = Math.max(0, bTop);
                const height = Math.min(overlayHeight, bTop + bH) - top;
                return <div className="cal-break-band" style={{ top: top + 'px', height: height + 'px' }}><span>{breakLabel}</span></div>;
              })()}

              {colAppts.map((a) => {
                const start = new Date(a.scheduledAt);
                const startMins = (start.getHours() - START_HOUR) * 60 + start.getMinutes();
                if (startMins < 0 || startMins >= (END_HOUR - START_HOUR) * 60) return null;

                const duration = Math.max(15, a.durationMinutes || 30);
                const top = (startMins / 60) * HOUR_HEIGHT;
                const remaining = Math.max(0, overlayHeight - top);
                const rawHeight = (duration / 60) * HOUR_HEIGHT - 2;
                const height = Math.max(22, Math.min(remaining, rawHeight));

                const colorTag = a.assignedUserColorTag ?? doctorTagById.get(a.assignedUserId);
                const docColor = colorForDoctor({ id: a.assignedUserId, colorTag });
                const statusMeta = STATUS_META[a.status] ?? STATUS_META.Scheduled;

                const laneInfo = lanes.get(a.id) ?? { lane: 0, totalLanes: 1 };
                const laneW = 100 / laneInfo.totalLanes;
                const laneL = laneInfo.lane * laneW;
                const laneLeftFrac = laneL / 100;
                const laneWidthFrac = laneW / 100;

                const isCompact = height < 40;
                const isMini    = height < 28;

                const docName = doctorNameById.get(a.assignedUserId);
                const showDoctorOnCard = !selectedDoctorId && !isDoctorView && docName;
                const isNoShow    = a.status === 'NoShow';
                const isCancelled = a.status === 'Cancelled';
                const isCompleted = a.status === 'Completed';

                return (
                  <div
                    key={`appt-${a.id}`}
                    className={[
                      'cal-appt',
                      isCompact ? 'cal-appt--compact' : '',
                      isMini ? 'cal-appt--mini' : '',
                      isCancelled ? 'cal-appt--cancelled' : '',
                      isNoShow    ? 'cal-appt--noshow'    : '',
                      isCompleted ? 'cal-appt--done'      : '',
                    ].filter(Boolean).join(' ')}
                    style={{
                      top: top + 'px',
                      height: height + 'px',
                      left: `calc((100% - var(--cal-right-gap)) * ${laneLeftFrac})`,
                      width: `calc((100% - var(--cal-right-gap)) * ${laneWidthFrac})`,
                      background: docColor.bg,
                      borderColor: docColor.border,
                      borderLeftColor: statusMeta.color,
                      color: docColor.text,
                    }}
                    draggable
                    onDragStart={(e) => {
                      try {
                        e.dataTransfer.setData('application/json', JSON.stringify({ id: a.id }));
                        e.dataTransfer.effectAllowed = 'move';
                      } catch (err) {
                        // ignore
                      }
                    }}
                    onClick={(e) => { e.stopPropagation(); onAppointmentClick(a, e); }}
                    title={`${a.patientName ?? a.leadName ?? 'Patient'} — ${docName ?? 'Unknown'} · ${fmtTime(start)} · ${fmtDurationShort(duration)} · ${statusLabel(a.status)}`}
                  >
                    <div className="cal-appt-head">
                      <span className="cal-appt-name">
                        {a.patientIsNew && <span className="cal-new-flag" title="New patient">(N)</span>}
                        {a.patientName ?? a.leadName ?? 'Patient'}
                      </span>
                      {/* Compact tiles are a single row — the status pill stays inline next to the time
                          since there's no separate "bottom" in a one-line layout. */}
                      {isCompact && !isMini && a.status !== 'Completed' && (
                        <span
                          className="cal-status-pill"
                          style={{ background: statusMeta.bg, color: statusMeta.text }}
                        >
                          {statusLabel(a.status)}
                        </span>
                      )}
                    </div>
                    {!isCompact && (
                      <div className="cal-appt-meta">
                        <span className="cal-appt-time">{fmtTime(start)} · {fmtDurationShort(duration)}</span>
                        {showDoctorOnCard && <span className="cal-appt-doc">{doctorDisplayName(docName)}</span>}
                      </div>
                    )}
                    {isCompact && (
                      <span className="cal-appt-time cal-appt-time--inline">
                        {fmtTime(start)} · {fmtDurationShort(duration)}
                      </span>
                    )}
                    {/* Status pill pinned to the absolute bottom of the tile (full-height tiles only)
                        so the patient name at the top never gets crowded or truncated. Hidden for
                        completed/checked-out appointments; details remain in the popover. */}
                    {!isCompact && a.status !== 'Completed' && (
                      <div className="cal-appt-footer">
                        <span
                          className="cal-status-pill"
                          style={{ background: statusMeta.bg, color: statusMeta.text }}
                        >
                          {statusLabel(a.status)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
